import type { TextVariants } from "@/components/ui/baseUi/text"
import { Text } from "@/components/ui/baseUi/text"
import { Language } from "@/modules/language/utils/languageUtils"
import { cn } from "@/modules/lib/utils"
import appIcon from "@images/appIcon.svg"
import { type UseCharterType, useCharterData } from "@modules/consumerAgreements/datas/useCharter.data"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import type { VariantProps } from "class-variance-authority"

type UseCharterPageProps = {
  className?: string
} & React.ComponentProps<"div">
export function UseCharterPage({ className, ...props }: UseCharterPageProps) {
  const language = useLanguage() === Language.FRENCH ? Language.FRENCH : Language.ENGLISH_US

  const formatTerms = (terms: UseCharterType[], parentIndex: string = "0") => {
    return terms.map((term, termIndex) => (
      <div key={termIndex} className={cn("flex flex-col items-start gap-5", className)} {...props}>
        <div className="flex flex-row gap-2">
          {term.index && <Text fontStyle="LL">{`${parentIndex === "0" ? "" : parentIndex + "."}${term.index}.`}</Text>}
          <div className="flex flex-col gap-5 [&_a]:break-all [&_a]:text-purple-500">
            {term.title && (
              <Text fontStyle="LL" className={parentIndex === "0" ? "underline" : ""}>
                {term.title}
              </Text>
            )}
            {term.content && formatTextIndentation(term.content, "BM")}
            {term.items &&
              term.items.length > 0 &&
              formatTerms(term.items, `${parentIndex === "0" ? "" : parentIndex + "."}${term.index}`)}
          </div>
        </div>
      </div>
    ))
  }

  function formatTextIndentation(
    text: string,
    fontStyle: VariantProps<typeof TextVariants>["fontStyle"],
    className?: string,
  ) {
    return text.split("\n").map((line, lineIndex) => (
      <Text key={lineIndex} fontStyle={fontStyle ?? "BM"} className={cn("text-justify leading-normal!", className)}>
        <div dangerouslySetInnerHTML={{ __html: line }} />
      </Text>
    ))
  }

  return (
    <div className="m-auto flex max-w-[960px] flex-col gap-2 px-7.5 py-12.5">
      <div className="flex flex-col gap-5">
        <div className="flex flex-col items-center gap-2 underline">
          {formatTextIndentation(useCharterData[language].title, "HM", "text-center")}
          <img src={appIcon} alt="appIcon" className="h-25 w-25" />
        </div>

        <div className="flex flex-row items-center gap-3">
          {formatTextIndentation(useCharterData[language].effectiveDateLabel, "LL")}
          {formatTextIndentation(useCharterData[language].effectiveDateValue, "BM")}
        </div>

        {formatTerms(useCharterData[language].terms)}
      </div>
    </div>
  )
}
